import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import trainingService, { TrainingComment, TrainingEntryView, TrainingWeekResponse } from '../services/trainingService';
import Snackbar from '../components/Snackbar';
import MenuButton from '../components/MenuButton';
import TrainingCommentModal from '../components/TrainingCommentModal';
import { linkify } from '../utils/linkify';
import { generateTrainingPDF } from '../utils/generateTrainingPDF';
import { useAppSelector } from '../store/hooks';

const getWeekStartMonday = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
};

const toDateOnlyString = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatDateNumber = (date: string) => new Date(`${date}T00:00:00`).getDate();
const formatMonthYear = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleString('en-US', { month: 'short', year: 'numeric' });

const getWeekNumber = (d: Date) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const SPORT_STYLES: Record<string, { label: string; cls: string; icon: string }> = {
    run:     { label: 'Run',     cls: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'fa-person-running' },
    swim:    { label: 'Swim',    cls: 'bg-cyan-100 text-cyan-700 border-cyan-200',       icon: 'fa-person-swimming' },
    bike:    { label: 'Bike',    cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: 'fa-person-biking' },
    weights: { label: 'Weights', cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'fa-dumbbell' },
    other:   { label: 'Other',   cls: 'bg-slate-100 text-slate-600 border-slate-200',   icon: 'fa-bolt' },
};

// Deterministic fallback palette for admin-created categories that have no
// hardcoded style. A stable hash of the key picks one, so the same category
// always looks the same. Keeps the badge from vanishing on unknown sports.
const FALLBACK_PALETTE = [
    'bg-emerald-100 text-emerald-700 border-emerald-200',
    'bg-rose-100 text-rose-700 border-rose-200',
    'bg-sky-100 text-sky-700 border-sky-200',
    'bg-amber-100 text-amber-700 border-amber-200',
    'bg-indigo-100 text-indigo-700 border-indigo-200',
    'bg-teal-100 text-teal-700 border-teal-200',
];

const titleCase = (key: string) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const sportStyle = (sport: string): { label: string; cls: string; icon: string } => {
    if (SPORT_STYLES[sport]) return SPORT_STYLES[sport];
    let hash = 0;
    for (let i = 0; i < sport.length; i++) hash = (hash * 31 + sport.charCodeAt(i)) & 0xffff;
    return { label: titleCase(sport), cls: FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length], icon: 'fa-tag' };
};

/**
 * Local stand-in for the placeholder entry the backend created to host a new
 * day-comment thread. Only the fields the grid reads are meaningful; it is
 * never sent back to the server, and the next week load replaces it with the
 * real row.
 */
const placeholderEntry = (
    comment: TrainingComment,
    date: string,
    userId: string,
): TrainingEntryView => ({
    id: comment.entryId,
    userId,
    trainerId: '',
    trainingDate: date,
    title: '',
    description: null,
    activityLink: null,
    userComment: null,
    sport: null,
    isPlaceholder: true,
    completed: false,
    durationHours: null,
    createdAt: comment.createdAt,
    updatedAt: comment.createdAt,
});

const TrainingSchedule: React.FC = () => {
    const location = useLocation();
    const navState = location.state as { openEntryId?: string; weekDate?: string } | null;
    const initialWeek = navState?.weekDate
        ? getWeekStartMonday(new Date(`${navState.weekDate}T00:00:00`))
        : getWeekStartMonday(new Date());

    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(initialWeek);
    const [weekData, setWeekData] = useState<TrainingWeekResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [commentsByEntryId, setCommentsByEntryId] = useState<Record<string, TrainingComment[]>>({});
    const [modalEntryId, setModalEntryId] = useState<string | null>(null);
    const [inputByEntryId, setInputByEntryId] = useState<Record<string, string>>({});
    const [sendingByEntryId, setSendingByEntryId] = useState<Record<string, boolean>>({});
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [weekNotes, setWeekNotes] = useState('');
    // Completion modal: the entry being marked done + the duration input.
    const [completeEntry, setCompleteEntry] = useState<TrainingEntryView | null>(null);
    const [durationInput, setDurationInput] = useState('');
    const [savingComplete, setSavingComplete] = useState(false);
    const { t } = useTranslation();
    const currentUser = useAppSelector((state) => state.auth.user);
    const pendingOpenEntryRef = useRef<string | null>(navState?.openEntryId ?? null);

    const weekDateParam = useMemo(() => toDateOnlyString(currentWeekStart), [currentWeekStart]);

    const loadWeek = async () => {
        try {
            setLoading(true);
            const response = await trainingService.getMyWeek(weekDateParam);
            setWeekData(response);

            const entryIds = response.days.flatMap((d) => d.entries.map((e) => e.id));

            if (entryIds.length > 0) {
                const results = await Promise.all(
                    entryIds.map((id) =>
                        trainingService.getComments(id).then((comments) => ({ id, comments }))
                    )
                );
                const map: Record<string, TrainingComment[]> = {};
                results.forEach(({ id, comments }) => { map[id] = comments; });
                setCommentsByEntryId(map);
            }

            // Open entry modal if navigated from a notification
            if (pendingOpenEntryRef.current) {
                setModalEntryId(pendingOpenEntryRef.current);
                pendingOpenEntryRef.current = null;
            }
        } catch (error: any) {
            const message = error?.response?.data?.message;
            const errorText = Array.isArray(message) ? message.join(', ') : message || t('trainingSchedule.errorLoad');
            setSnackbar({ type: 'error', message: errorText });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadWeek();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekDateParam]);

    // Escape closes the completion modal, matching the other modals in the app.
    useEffect(() => {
        if (!completeEntry) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !savingComplete) setCompleteEntry(null);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [completeEntry, savingComplete]);

    const handlePrevWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentWeekStart(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentWeekStart(newDate);
    };

    const removeComment = (entryId: string, commentId: string) =>
        setCommentsByEntryId((prev) => ({
            ...prev,
            [entryId]: (prev[entryId] ?? []).filter((c) => c.id !== commentId),
        }));

    const handleDeleteComment = async (entryId: string, commentId: string) => {
        if (!window.confirm('Delete this message?')) return;
        try {
            await trainingService.deleteComment(commentId);
            removeComment(entryId, commentId);
        } catch {
            // ignore
        }
    };

    const handleSendComment = async (entryId: string) => {
        const msg = (inputByEntryId[entryId] || '').trim();
        if (!msg) return;
        try {
            setSendingByEntryId((prev) => ({ ...prev, [entryId]: true }));
            await trainingService.addComment(entryId, msg);
            setInputByEntryId((prev) => ({ ...prev, [entryId]: '' }));
            const updated = await trainingService.getComments(entryId);
            setCommentsByEntryId((prev) => ({ ...prev, [entryId]: updated }));
        } catch {
            // ignore
        } finally {
            setSendingByEntryId((prev) => ({ ...prev, [entryId]: false }));
        }
    };

    /**
     * Comment on a day with no exercise yet. There is no entry to key off, so
     * the draft is keyed by date and the backend creates a placeholder entry to
     * host the thread. The created comment carries that entry's id, so the new
     * thread is grafted into local state rather than refetching the whole week —
     * otherwise the first comment on an empty day would flash the grid away.
     */
    const handleSendDayComment = async (date: string) => {
        const msg = (inputByEntryId[date] || '').trim();
        if (!msg || !currentUser) return;
        try {
            setSendingByEntryId((prev) => ({ ...prev, [date]: true }));
            const created = await trainingService.addDayComment(currentUser.id, date, msg);
            setInputByEntryId((prev) => ({ ...prev, [date]: '' }));
            setCommentsByEntryId((prev) => ({
                ...prev,
                [created.entryId]: [...(prev[created.entryId] ?? []), created],
            }));
            // Attach the placeholder to its day so the comment box switches over
            // to the entry-keyed path for every subsequent message.
            setWeekData((prev) =>
                prev
                    ? {
                        ...prev,
                        days: prev.days.map((d) =>
                            d.date === date && !d.entries.some((e) => e.id === created.entryId)
                                ? { ...d, entries: [...d.entries, placeholderEntry(created, date, currentUser.id)] }
                                : d,
                        ),
                    }
                    : prev,
            );
        } catch (error: any) {
            const message = error?.response?.data?.message;
            setSnackbar({
                type: 'error',
                message: Array.isArray(message) ? message.join(', ') : message || t('trainingSchedule.errorSaveComment'),
            });
        } finally {
            setSendingByEntryId((prev) => ({ ...prev, [date]: false }));
        }
    };

    const openCompleteModal = (entry: TrainingEntryView) => {
        setCompleteEntry(entry);
        setDurationInput(entry.durationHours != null ? String(entry.durationHours) : '');
    };

    const applyEntryUpdate = (updated: TrainingEntryView) => {
        setWeekData((prev) =>
            prev
                ? {
                    ...prev,
                    days: prev.days.map((d) => ({
                        ...d,
                        entries: d.entries.map((e) => (e.id === updated.id ? updated : e)),
                    })),
                }
                : prev,
        );
    };

    const submitCompletion = async (markDone: boolean) => {
        if (!completeEntry) return;
        const raw = durationInput.trim().replace(',', '.');
        let hours: number | undefined;
        if (markDone && raw) {
            const parsed = Number(raw);
            if (Number.isNaN(parsed) || parsed < 0 || parsed > 24) {
                setSnackbar({ message: t('trainingSchedule.invalidDuration'), type: 'error' });
                return;
            }
            hours = Math.round(parsed * 100) / 100;
        }
        try {
            setSavingComplete(true);
            const updated = await trainingService.markComplete(completeEntry.id, markDone, hours);
            applyEntryUpdate(updated);
            // Refresh week stats (completed count + hours trained) from server.
            const refreshed = await trainingService.getMyWeek(weekDateParam);
            setWeekData(refreshed);
            setCompleteEntry(null);
        } catch {
            setSnackbar({ message: t('trainingSchedule.completionFailed'), type: 'error' });
        } finally {
            setSavingComplete(false);
        }
    };

    const weekNumber = getWeekNumber(currentWeekStart);
    const weekEndLabel = weekData
        ? new Date(`${weekData.weekEnd}T00:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' })
        : currentWeekStart.toLocaleString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background">
            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}
            {/* Five controls cannot share one row at 375px, so on mobile the header
                is two deliberate rows: menu + title + PDF, then the week stepper
                across the full width. From md it collapses back to the original
                single row. */}
            <header className="bg-primary border-b border-cyan-600 px-3 md:px-8 py-2.5 md:py-0 md:h-24 md:flex md:items-center shadow-md z-20 sticky top-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-6 w-full md:justify-between">

                    {/* Row 1 (mobile) */}
                    <div className="flex items-center justify-between gap-2 md:hidden">
                        <div className="flex items-center gap-2 min-w-0">
                            <MenuButton />
                            <span className="text-sm font-bold text-white truncate">
                                {t('nav.trainingSchedule')}
                            </span>
                        </div>
                        <button
                            onClick={() => weekData && generateTrainingPDF(weekData)}
                            disabled={!weekData}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 flex-shrink-0 bg-white text-primary rounded font-bold shadow-md disabled:opacity-50"
                        >
                            <i className="fa-solid fa-file-pdf text-sm"></i>
                            <span className="text-[11px]">{t('trainingSchedule.createPdf')}</span>
                        </button>
                    </div>

                    {/* Week stepper — row 2 on mobile, inline at md+ */}
                    <div className="flex items-center gap-2 md:gap-6 min-w-0">
                        <div className="hidden md:block"><MenuButton /></div>
                        <div className="flex items-center gap-2 min-w-0 w-full md:w-auto">
                            <button
                                onClick={handlePrevWeek}
                                aria-label={t('trainingSchedule.week')}
                                className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent hover:bg-lime-500 text-white shadow-lg transition-all border-2 border-lime-400"
                            >
                                <i className="fa-solid fa-arrow-left text-sm md:text-lg"></i>
                            </button>
                            <div className="bg-white/10 backdrop-blur-md px-2 md:px-6 py-1.5 md:py-2 rounded-lg border border-white/20 min-w-0 flex-1 md:flex-none">
                                <h1 className="text-xs md:text-xl font-bold text-white flex items-center justify-center md:justify-start gap-1.5 md:gap-3 whitespace-nowrap overflow-hidden">
                                    <span className="truncate">
                                        {weekData
                                            ? `${formatDateNumber(weekData.weekStart)} - ${formatDateNumber(weekData.weekEnd)} ${weekEndLabel}`
                                            : t('trainingSchedule.loadingWeek')}
                                    </span>
                                    <span className="bg-white text-primary text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded shadow-sm flex-shrink-0">{t('trainingSchedule.week')} {weekNumber}</span>
                                    {weekData?.programLabel && (
                                        <span className="hidden sm:inline bg-accent text-white text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded shadow-sm whitespace-nowrap flex-shrink-0">
                                            {weekData.programLabel}
                                        </span>
                                    )}
                                </h1>
                            </div>
                            <button
                                onClick={handleNextWeek}
                                aria-label={t('trainingSchedule.week')}
                                className="w-9 h-9 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent hover:bg-lime-500 text-white shadow-lg transition-all border-2 border-lime-400"
                            >
                                <i className="fa-solid fa-arrow-right text-sm md:text-lg"></i>
                            </button>
                        </div>
                    </div>

                    {/* Right actions — desktop only; PDF lives in row 1 on mobile. */}
                    <div className="hidden md:flex items-center gap-4">
                        <div className="hidden lg:flex items-center relative">
                            <input
                                type="text"
                                placeholder={t('trainingSchedule.searchPlaceholder')}
                                className="pl-4 pr-20 py-2.5 bg-white/10 border border-white/35 rounded shadow-inner text-white placeholder-white/70 w-64 outline-none focus:border-white/60"
                            />
                            <button className="absolute right-1 top-1 bottom-1 px-4 bg-white text-primary rounded font-semibold text-sm">
                                {t('trainingSchedule.searchBtn')}
                            </button>
                        </div>
                        <button
                            onClick={() => weekData && generateTrainingPDF(weekData)}
                            disabled={!weekData}
                            className="flex items-center gap-2 px-4 py-2.5 flex-shrink-0 bg-white text-primary rounded font-bold hover:shadow-lg hover:bg-slate-50 transition-all shadow-md border-b-4 border-slate-200 active:border-b-0 active:translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i className="fa-solid fa-file-pdf text-lg"></i>
                            <span className="text-sm">{t('trainingSchedule.createPdf')}</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-3 lg:p-6 relative bg-slate-50">
                <div className="max-w-[1600px] mx-auto h-full flex flex-col">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-3 md:mb-6">
                        <div className="bg-white rounded-lg p-3 md:p-4 border-l-4 border-primary shadow-sm">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('trainingSchedule.statsTotal')}</p>
                            <p className="text-2xl font-black text-slate-700">{weekData?.stats.totalSessions ?? 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 md:p-4 border-l-4 border-accent shadow-sm">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('trainingSchedule.statsCompleted')}</p>
                            <p className="text-2xl font-black text-slate-700">{weekData?.stats.completedSessions ?? 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 md:p-4 border-l-4 border-highlight shadow-sm">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('trainingSchedule.statsUpcoming')}</p>
                            <p className="text-2xl font-black text-slate-700">{weekData?.stats.upcomingSessions ?? 0}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 md:p-4 border-l-4 border-purple-500 shadow-sm">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('trainingSchedule.statsHours')}</p>
                            <p className="text-2xl font-black text-slate-700">{weekData?.stats.hoursTrained ?? 0}<span className="text-base font-bold text-slate-400">h</span></p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 h-full">
                        <div className="xl:col-span-9 bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
                            <div className="hidden md:grid grid-cols-12 gap-6 px-6 py-3 bg-gradient-to-r from-cyan-400 to-cyan-500 border-b border-cyan-500 text-xs font-black text-white uppercase tracking-wider">
                                <div className="col-span-2">{t('trainingSchedule.colDay')}</div>
                                <div className="col-span-6">{t('trainingSchedule.colPlan')}</div>
                                <div className="col-span-4">{t('trainingSchedule.colFeedback')}</div>
                            </div>

                            {loading ? (
                                <div className="py-20 text-center text-slate-400">
                                    <i className="fa-solid fa-spinner fa-spin text-3xl"></i>
                                </div>
                            ) : (
                                weekData?.days.map((day) => {
                                    // Placeholder entries carry a comment thread but no exercise,
                                    // so a day holding only those still reads as "no exercise".
                                    const sessions = day.entries.filter((e) => !e.isPlaceholder);
                                    const placeholder = day.entries.find((e) => e.isPlaceholder);
                                    const isEmpty = sessions.length === 0;
                                    // Where an empty day's comments live: the placeholder's thread
                                    // if one exists, otherwise a date-keyed draft that creates it.
                                    const emptyDayKey = placeholder?.id ?? day.date;
                                    const emptyDayComments = placeholder ? (commentsByEntryId[placeholder.id] ?? []) : [];

                                    return (
                                        <div
                                            key={day.date}
                                            className={`p-3 md:p-5 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-start border-b border-slate-100 ${day.isToday ? 'bg-cyan-50/50' : 'hover:bg-cyan-50/30'}`}
                                        >
                                            {/* Day column */}
                                            <div className={`md:col-span-2 flex md:flex-col items-center md:items-start gap-2 md:gap-0 pt-2 ${day.isToday ? 'pl-2' : ''}`}>
                                                <span className={`text-xs font-bold uppercase tracking-wider mb-1 ${day.isToday ? 'text-primary' : 'text-slate-500'}`}>
                                                    {day.isToday ? t('trainingSchedule.today') : day.dayName}
                                                </span>
                                                <div className="flex items-baseline gap-1">
                                                    <span className={`text-xl font-bold ${day.isToday ? 'text-primary' : 'text-slate-800'}`}>
                                                        {formatDateNumber(day.date)}
                                                    </span>
                                                    <span className="text-sm text-slate-400">{formatMonthYear(day.date)}</span>
                                                </div>
                                            </div>

                                            {/* Sessions column — one row per session */}
                                            <div className="md:col-span-10 flex flex-col gap-4">
                                                {isEmpty ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-10 gap-6 items-start">
                                                        <div className="md:col-span-6">
                                                            <div className="bg-white border-2 border-slate-100 rounded-lg p-4 min-h-[100px] flex items-center justify-center text-slate-400 text-sm font-medium">
                                                                {day.isRestDay ? t('trainingSchedule.restDay') : t('trainingSchedule.noExercise')}
                                                            </div>
                                                        </div>
                                                        <div className="md:col-span-4 flex flex-col gap-3">
                                                            {day.isRestDay && (
                                                                <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center text-slate-400 text-sm font-medium">
                                                                    {t('trainingSchedule.restDayMsg')}
                                                                </div>
                                                            )}
                                                            {/* Comments work even with no exercise assigned, so the
                                                                athlete can still ask the trainer for tips. */}
                                                            <div className="rounded-lg border bg-cyan-50/30 border-cyan-100 flex flex-col gap-2 px-3 py-2.5">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                                        {t('trainingSchedule.comments')}
                                                                    </span>
                                                                    {emptyDayComments.length > 2 && placeholder && (
                                                                        <button
                                                                            onClick={() => setModalEntryId(placeholder.id)}
                                                                            title={t('trainingSchedule.viewConversation')}
                                                                            className="w-6 h-6 flex items-center justify-center rounded-full bg-cyan-50 hover:bg-cyan-100 text-cyan-500 border border-cyan-200 transition-colors"
                                                                        >
                                                                            <i className="fa-solid fa-clock-rotate-left text-[10px]"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {emptyDayComments.length === 0 ? (
                                                                    <p className="text-xs text-slate-400 italic py-1">{t('trainingSchedule.noComments')}</p>
                                                                ) : (
                                                                    emptyDayComments.slice(-2).map((c) => (
                                                                        <div key={c.id} className={`flex flex-col gap-0.5 ${c.userId === currentUser?.id ? 'items-end' : 'items-start'}`}>
                                                                            <span className="text-[10px] text-slate-400">{c.userId === currentUser?.id ? t('trainingSchedule.you') : c.senderName}{c.userId === currentUser?.id && (<button onClick={() => handleDeleteComment(placeholder!.id, c.id)} title="Delete message" className="ml-1.5 text-slate-400 hover:text-red-500"><i className="fa-solid fa-trash-can"></i></button>)}</span>
                                                                            <span className={`text-xs px-2.5 py-1.5 rounded-xl max-w-[90%] leading-relaxed ${c.userId === currentUser?.id ? 'bg-cyan-500 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                                                                                {linkify(c.message)}
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <input
                                                                        type="text"
                                                                        value={inputByEntryId[emptyDayKey] || ''}
                                                                        onChange={(e) => setInputByEntryId((prev) => ({ ...prev, [emptyDayKey]: e.target.value }))}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key !== 'Enter') return;
                                                                            placeholder ? handleSendComment(placeholder.id) : handleSendDayComment(day.date);
                                                                        }}
                                                                        placeholder={t('trainingSchedule.writeCommentShort')}
                                                                        className="flex-1 text-base md:text-xs rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 bg-white placeholder-slate-400"
                                                                    />
                                                                    <button
                                                                        onClick={() => (placeholder ? handleSendComment(placeholder.id) : handleSendDayComment(day.date))}
                                                                        disabled={!inputByEntryId[emptyDayKey]?.trim() || sendingByEntryId[emptyDayKey]}
                                                                        className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                                    >
                                                                        {sendingByEntryId[emptyDayKey]
                                                                            ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                                                            : <i className="fa-solid fa-paper-plane text-xs"></i>
                                                                        }
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    sessions.map((entry, idx) => {
                                                        const entryComments = commentsByEntryId[entry.id] ?? [];
                                                        const previewComments = entryComments.slice(-2);
                                                        return (
                                                            <div
                                                                key={entry.id}
                                                                className={`grid grid-cols-1 md:grid-cols-10 gap-6 items-start ${idx > 0 ? 'pt-4 border-t border-dashed border-slate-200' : ''}`}
                                                            >
                                                                {/* Exercise card */}
                                                                <div className="md:col-span-6">
                                                                    <div className={`bg-white border-2 rounded-lg p-4 min-h-[100px] relative shadow-sm ${day.isToday ? 'border-primary/20' : 'border-slate-100'}`}>
                                                                        <div className="flex items-start gap-3">
                                                                            <div className="w-8 h-8 rounded bg-cyan-100 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                                                                <i className="fa-solid fa-dumbbell text-sm"></i>
                                                                            </div>
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                                    {entry.sport && (
                                                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${sportStyle(entry.sport).cls}`}>
                                                                                            <i className={`fa-solid ${sportStyle(entry.sport).icon} text-[10px]`}></i>
                                                                                            {sportStyle(entry.sport).label}
                                                                                        </span>
                                                                                    )}
                                                                                    {entry.completed && (
                                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border bg-cyan-100 text-cyan-700 border-cyan-200">
                                                                                            <i className="fa-solid fa-check text-[10px]"></i>
                                                                                            {t('trainingSchedule.done')}
                                                                                            {entry.durationHours != null && ` · ${entry.durationHours}h`}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <p className="text-slate-600 text-sm leading-relaxed">{entry.description || '-'}</p>
                                                                            </div>
                                                                            <button
                                                                                onClick={() => openCompleteModal(entry)}
                                                                                title={entry.completed ? t('trainingSchedule.editCompletion') : t('trainingSchedule.markDone')}
                                                                                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${entry.completed
                                                                                    ? 'bg-cyan-500 border-cyan-500 text-white hover:bg-cyan-600'
                                                                                    : 'bg-white border-slate-200 text-slate-300 hover:border-cyan-400 hover:text-cyan-500'}`}
                                                                            >
                                                                                <i className="fa-solid fa-check text-sm"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Activity link + comments */}
                                                                <div className="md:col-span-4 flex flex-col gap-3">
                                                                    <div className={`relative px-3 py-2.5 rounded-lg flex items-center gap-3 ${day.isToday ? 'bg-white border border-primary/30' : 'bg-cyan-50/30 border border-cyan-100'}`}>
                                                                        <i className={`fa-solid fa-link text-sm ${day.isToday ? 'text-primary' : 'text-cyan-400'}`}></i>
                                                                        <input
                                                                            type="text"
                                                                            value={entry.activityLink || ''}
                                                                            readOnly
                                                                            placeholder={t('trainingSchedule.noActivityLink')}
                                                                            className={`bg-transparent border-0 p-0 text-sm w-full focus:ring-0 ${day.isToday ? 'placeholder-slate-400 text-slate-800' : 'placeholder-cyan-300 text-cyan-900'}`}
                                                                        />
                                                                    </div>

                                                                    <div className={`rounded-lg border flex flex-col gap-2 px-3 py-2.5 ${day.isToday ? 'bg-white border-primary/20' : 'bg-cyan-50/30 border-cyan-100'}`}>
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Comments</span>
                                                                            {entryComments.length > 2 && (
                                                                                <button
                                                                                    onClick={() => setModalEntryId(entry.id)}
                                                                                    title="View full conversation"
                                                                                    className="w-6 h-6 flex items-center justify-center rounded-full bg-cyan-50 hover:bg-cyan-100 text-cyan-500 border border-cyan-200 transition-colors"
                                                                                >
                                                                                    <i className="fa-solid fa-clock-rotate-left text-[10px]"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        {previewComments.length === 0 ? (
                                                                            <p className="text-xs text-slate-400 italic py-1">No comments yet</p>
                                                                        ) : (
                                                                            previewComments.map((c) => (
                                                                                <div key={c.id} className={`flex flex-col gap-0.5 ${c.userId === currentUser?.id ? 'items-end' : 'items-start'}`}>
                                                                                    <span className="text-[10px] text-slate-400">{c.userId === currentUser?.id ? 'You' : c.senderName}{c.userId === currentUser?.id && (<button onClick={() => handleDeleteComment(entry.id, c.id)} title="Delete message" className="ml-1.5 text-slate-400 hover:text-red-500"><i className="fa-solid fa-trash-can"></i></button>)}</span>
                                                                                    <span className={`text-xs px-2.5 py-1.5 rounded-xl max-w-[90%] leading-relaxed ${c.userId === currentUser?.id ? 'bg-cyan-500 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                                                                                        {linkify(c.message)}
                                                                                    </span>
                                                                                </div>
                                                                            ))
                                                                        )}
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <input
                                                                                type="text"
                                                                                value={inputByEntryId[entry.id] || ''}
                                                                                onChange={(e) => setInputByEntryId((prev) => ({ ...prev, [entry.id]: e.target.value }))}
                                                                                onKeyDown={(e) => { if (e.key === 'Enter') handleSendComment(entry.id); }}
                                                                                placeholder="Write a comment…"
                                                                                className="flex-1 text-base md:text-xs rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 bg-white placeholder-slate-400"
                                                                            />
                                                                            <button
                                                                                onClick={() => handleSendComment(entry.id)}
                                                                                disabled={!inputByEntryId[entry.id]?.trim() || sendingByEntryId[entry.id]}
                                                                                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                                            >
                                                                                {sendingByEntryId[entry.id]
                                                                                    ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                                                                    : <i className="fa-solid fa-paper-plane text-xs"></i>
                                                                                }
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="xl:col-span-3">
                            <div className="bg-white rounded-xl border border-gray-200 p-0 h-[600px] sticky top-0 shadow-sm flex flex-col overflow-hidden">
                                <div className="p-4 border-b border-cyan-100 bg-cyan-50 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <i className="fa-regular fa-clipboard text-slate-400"></i>
                                        <h3 className="font-bold text-secondary">{t('trainingSchedule.weekNotes', { week: weekNumber })}</h3>
                                    </div>
                                    <button className="text-slate-400 hover:text-primary text-sm font-medium">Save</button>
                                </div>
                                <div className="flex-1 bg-cyan-50/30 p-4">
                                    <textarea
                                        value={weekNotes}
                                        onChange={(e) => setWeekNotes(e.target.value)}
                                        className="w-full h-full bg-transparent border-0 text-slate-600 text-sm leading-relaxed focus:ring-0 resize-none p-0 placeholder-slate-400"
                                        placeholder={t('trainingSchedule.weekNotesPlaceholder')}
                                    />
                                </div>
                                <div className="p-3 border-t border-gray-100 bg-white text-xs text-slate-400 flex justify-between">
                                    <span>{t('trainingSchedule.draftNotes')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comment modal */}
            {modalEntryId && currentUser && (() => {
                const entry = weekData?.days.flatMap((d) => d.entries).find((e) => e.id === modalEntryId);
                return (
                    <TrainingCommentModal
                        entryId={modalEntryId}
                        entryTitle={entry?.title ?? ''}
                        currentUserId={currentUser.id}
                        onClose={() => setModalEntryId(null)}
                        onDeleted={(id) => removeComment(modalEntryId, id)}
                    />
                );
            })()}

            {completeEntry && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
                    onClick={() => !savingComplete && setCompleteEntry(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90dvh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-cyan-500 to-blue-500 flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-check text-white text-sm"></i>
                                </div>
                                <div>
                                    <p className="text-xs text-white/70 font-medium uppercase tracking-wider">
                                        {completeEntry.sport ? sportStyle(completeEntry.sport).label : t('trainingSchedule.session')}
                                    </p>
                                    <h3 className="text-white font-bold text-sm leading-tight">
                                        {t('trainingSchedule.markDone')}
                                    </h3>
                                </div>
                            </div>
                            <button
                                onClick={() => setCompleteEntry(null)}
                                disabled={savingComplete}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors disabled:opacity-50"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-4 md:px-6 py-4 md:py-5 flex flex-col gap-1.5 overflow-y-auto">
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {t('trainingSchedule.durationLabel')}
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    step="0.25"
                                    min="0"
                                    max="24"
                                    autoFocus
                                    value={durationInput}
                                    onChange={(e) => setDurationInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && submitCompletion(true)}
                                    placeholder="1.5"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 pr-16 text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">
                                    {t('trainingSchedule.hoursUnit')}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">{t('trainingSchedule.durationOptional')}</p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
                            {completeEntry.completed && (
                                <button
                                    onClick={() => submitCompletion(false)}
                                    disabled={savingComplete}
                                    className="px-3 py-2 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-60"
                                >
                                    {t('trainingSchedule.markNotDone')}
                                </button>
                            )}
                            <div className="flex-1"></div>
                            <button
                                onClick={() => setCompleteEntry(null)}
                                disabled={savingComplete}
                                className="px-5 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-60"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={() => submitCompletion(true)}
                                disabled={savingComplete}
                                className="px-5 py-2 rounded-lg text-sm font-semibold bg-cyan-500 hover:bg-cyan-600 text-white transition-colors flex items-center gap-2 disabled:opacity-60"
                            >
                                {savingComplete ? (
                                    <><i className="fa-solid fa-spinner fa-spin"></i> {t('common.saving')}</>
                                ) : (
                                    <><i className="fa-solid fa-floppy-disk"></i> {t('common.save')}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingSchedule;
